/**
 * 跨遊戲共用 i18n key（中文）
 *
 * 掛在 `common.*` namespace 下，由 src/locales/index.ts 統一聚合。
 * 用途：放「不管哪款遊戲都會用到」的 host UI 詞彙（modal 標題、按鈕之類）。
 *
 * 遊戲內的 paytable 符號名稱 / rules 文字現在統一在
 * src/games/<gameId>/content.ts 用 I18nText 物件單一檔承載，不再放這裡。
 */

export default {
  paytable: '赔付表',
  rules: '游戏规则',

  /**
   * 玩家看得到的错误文案（`common.errors.*`）。
   *
   * ⚠️ 不要把 `ApiError.message` 直接设进画面 state —— 那是后端原文，或 http.ts 产生的
   * `'Request timed out'` / `'Unauthorized'` / `'HTTP 502'` 这类给工程师看的英文。
   * 转换统一走 `src/api/errorMessage.ts` 的 `toUserMessage()`。
   *
   * ⚠️ 这个 namespace 必须放在**本档**。`src/locales/index.ts` 是
   * `{ ...zhMain, common: zhCommon }` —— `common` 会整个被本档覆盖，
   * 所以写在 auto-generated 的 `src/locales/zh.ts` 里的 `common.*` 一律取不到。
   */
  /**
   * 對接商「盤面回放」根元件（src/replay/ResultDetailApp.vue）的狀態文案。
   * 這條路徑沒有登入、沒有 Cocos iframe，四種 phase 各一句。
   */
  replay: {
    loading: '载入盘面中…',
    /** gameId 不在 REGISTERED_GAME_IDS（host 尚未接這款）—— 產品限制，非暫時性錯誤 */
    unsupported: '此游戏尚未支持盘面回放',
    loadFailed: '盘面载入失败',
    missingParams: '回放链接缺少必要参数（record_id / token / gameId）',
    /** 已註冊卻解不出元件（chunk 載入失敗）時，接在 loadFailed 後面的提示 */
    refreshHint: '请刷新页面',
  },

  /**
   * 遊戲載入「軟逾時」對話框（`SlowNetworkDialog.vue`）。
   * 逾時**不中斷**玩家 —— 只跳這個框讓他選「刷新」或「等待」，選等待就繼續原本的載入。
   * 文案逐字對齊競品（2026-08-18）。
   */
  slowNetwork: {
    message: '您的网络有点慢，点击刷新可能更快或继续等待？',
    refresh: '刷新',
    wait: '等待',
  },
  /**
   * 新 loading 遮罩（docs/LOADING_PROTOCOL.md）。
   * 🛑 進度條下方是**階段文字**（競品 `#tips-text` 第一個 span，如「正在登录」），
   *    **不是**「加载中 N%」—— 競品把百分比放在第二個 span 且預設 `-hidden`。
   *    這幾句是 host **真實**知道的階段（只有百分比是假的）。
   */
  /**
   * 新 loading 遮罩（docs/LOADING_PROTOCOL.md）。**文案逐字對齊競品**（2026-08-20 使用者提供）。
   * 🛑 進度條下方是**階段文字**（競品 `#tips-text` 第一個 span），不是「加载中 N%」；
   *    百分比只掛在「正在加载资源」那一段（其餘階段競品是 `-hidden`）。
   */
  loading: {
    phase: {
      /** 尚未拿到 token */
      login: '正在登录',
      /** 已登入、等 iframe + GAME_READY */
      game: '正在读取信息',
      /** GAME_READY 後、等 LOAD_COMPLETE（新款限定）—— **只有這段帶百分比** */
      assets: '正在加载资源',
      /** 進度補滿 100%、等玩家按開始 */
      complete: '完成',
    },
    /** 百分比（競品 `[50%]` 格式；只接在 `phase.assets` 後面） */
    percent: '[{n}%]',
    /**
     * 進度條下方的**輪播小提示**（競品 `#animated_text`，與階段文字是**兩個不同的東西**）。
     * 逐字對齊競品（2026-08-20 使用者提供 6 句）。加減句子 = 改這裡 + `LoadingScreen.vue` 的 `TIP_KEYS`。
     */
    tips: {
      device: '使用新版本的手机系统可以获得最佳的游戏体验！',
      idlePages: '关闭闲置网页，给我多点空间，还你更多惊喜！',
      wifi: '高清游戏资源可能略多，建议在WIFI环境下预加载！',
      browser: '在Safari和Chrome享受更震撼的游戏体验吧！',
      cache: '加载过的游戏会在浏览器保留缓存，下一次加载更流畅！',
      firstLoad: '亲，请稍安勿躁！第一次加载可能略微需要一点时间哦！',
    },
    /** 資源載完後取代進度條的按鈕（競品 `#get-started-text` 逐字：**开始**，不是「开始游戏」） */
    start: '开始',
  },

  errors: {
    // 依错误分类（errorMessage.ts 的 ErrorKind）
    timeout: '连线逾时，请稍后再试',
    network: '网络连线异常，请检查网络后再试',
    server: '服务器忙碌，请稍后再试',
    loadFailed: '载入失败，请重试',
    // 登录 / 认证流程
    missingTicket: '缺少 ticket 参数，请重新进入游戏',
    loginFailed: '登录失败，请重新进入游戏',
    ticketInvalid: '登录凭证已失效，请重新进入游戏',
    verifyFailed: '验证失败，请重新进入游戏',
    sessionExpired: '登录已失效，请关闭页面后重新进入游戏',
    // 余额弹窗（BalanceModal）
    fetchFailed: '获取余额失败，请重试',
    missingLogin: '缺少登录信息',
    networkError: '网络错误',
    tokenExpired: '登录已失效',
  },

  history: {
    chooseDateRange: '选择日期范围',
    title: '历史记录',
    today: '今天',
    last7days: '最近 7 天',
    custom: '自定',
    start: '开始',
    end: '结束',
    confirm: '确定',
    winInfo: '赢奖情况',
    noWin: '无中奖组合',
    /** {count}星连珠：n 連描述（3, 4, 5, 6...） */
    xOfAKind: '{count}星连珠',
    /** {count}条中奖路：ways 路數 */
    way: '{count}条中奖路',
    /** 雙向 ways 遊戲（ryxc）：該筆 award 從左往右算 */
    wayLeftToRight: '从左到右: {count}条中奖路',
    /** 雙向 ways 遊戲（ryxc）：該筆 award 從右往左算 */
    wayRightToLeft: '从右到左: {count}条中奖路',
    /** 现金奖励 Pay Anywhere 純獎金總額） */
    cashPrize: '现金奖励',
    /** (i) tooltip 上方的公式說明 */
    formulaLabel: '投注大小 x 投注倍数 x 符号赔付值 x 中奖路',
    /** (i) 線遊戲（JQT）公式說明：無中奖路，赔付值已含連線數量檔位 */
    formulaLabelLine: '投注大小 x 投注倍数 x 符号赔付值',
    // ── MULTIPLIER variant（HBFY 倍率結算）─────────────
    /** 奖金倍数 ×N */
    bonusMultiplier: '奖金倍数',
    /** 倍数 +N（倍率累加型 FG：本轉新增倍率，顯示在奖金倍数上方） */
    multiplierAdded: '倍数',
    /** 奖金倍数 +N（翻倍夺宝：scatter row 右側，每 scatter +2；JSMJT） */
    multiplierGain: '奖金倍数 +{n}',
    /** (i)奖金倍数 */
    bonusMultiplierTooltip: '奖金倍数',
    /** 倍数奖金 = 倍率加成的部分 */
    multiplierWin: '倍数奖金',
    /** 共赢得奖金 = 最終派彩 */
    totalWinAccrued: '共赢得奖金',
    /**
     * 共赢得 = 最終派彩（**短版**，比 `totalWinAccrued` 少「奖金」兩字）。
     * 競品逐字用哪版是 per-game 的，不要互相取代（HJYL 結算帧 ×1 用這版）。
     *
     * ⚠️ 跟 per-game 的 `content.history.labels.totalWin` **不是同一件事**，
     * 也不是它的 fallback —— `labels.totalWin` 沒設時的預設仍是 `totalWinAccrued`。
     * 要用這支請走 `MultiplierBlockVM.totalLabelKey`。
     */
    totalWin: '共赢得',
    /** 倍数已激活 ×N（ANBS：某欄激活且含阿努比斯时顯示） */
    multiplierActivated: '倍数已激活',
    /** 收集（zcm：收集 N 隻招财猫 badge 副標） */
    collected: '收集',
    // ── SCATTER / FREE_SPIN variant（夺宝符号触发免费旋转）─────
    /** {count}次免费旋转：scatter trigger award 右側顯示 */
    freeSpinAwarded: '{count}次免费旋转',
    /** +{count} 免费旋转：FG 內 retrigger(KLDG +1 符號加注)右側顯示 */
    freeSpinRetrigger: '+{count} 免费旋转',
    // ── DetailHeader 標題 ─────────────────────────────
    /** record.pid === 0 / null 時的標題（普通 spin） */
    normalSpin: '普通旋转',
    /** record.pid 為非零值時的標題（免費旋轉 child） */
    freeSpin: '免费旋转',
    /**
     * 免費旋轉 {n}/{total}：FG group header / dropdown 的**預設**文案。
     * 各遊戲若要自訂名稱（JQT 财富旋转 / SBJN 重新旋转 …）改在
     * content.ts → history.labels.freeSpin 內聯 I18nText，不在這加 key。
     */
    freeSpinIndex: '免费旋转 {n}/{total}',
    /** Dropdown 底部關閉按鈕 */
    close: '关闭',
    /** HistoryTable 列表 empty state */
    noRecords: '没有历史记录',
    /** HistoryTable 欄頭：时间 */
    time: '时间',
    /** HistoryTable 列表 loading 文案 */
    loading: '加载中',
    /** HistoryTable footer：{count} 条记录 */
    recordsCount: '{count} 条记录',
    // ── StatsBar 4 欄 label ──────────────────────────
    transactionId: '交易单号',
    /** 點交易单号複製成功後的 vant toast 文案（label 本身不變） */
    copied: '已复制',
    bet: '投注',
    profit: '盈利',
    balance: '余额',
    // ── RoundTabs ────────────────────────────────────
    /** 第 N 回合，{n} 是當前回合數 */
    roundN: '第{n}回合',
    betSize: '投注大小',
    betMultiplier: '投注倍数',
    fetchFailed: '获取历史记录失败，请重试',
  },
}
